import { forwardRef, useEffect, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { KeyValue } from '@mews/enums';
import { InputValue, useComponentProps } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { BareIconButton } from '../BareIconButton';
import { Field } from '../Field';
import { Input } from '../Input';
import { ContainerElement, CounterButtonWrapper, FlexInputContainer } from './style';
import { HandleStepperValueChange, StepperEvent, StepperHandleChange, StepperProps } from './types';

const DEFAULT_MAX_LIMIT = 999999999.99;
const DEFAULT_MIN_LIMIT = -999999999.99;

enum MouseEventAction {
    DECREMENT = 'decrement',
    INCREMENT = 'increment',
}

const MOUSE_DOWN_INITIAL_CHANGE_DELAY = 350;
const MOUSE_DOWN_INTERVAL_CHANGE_SPEED = 40;

const StepperElement: React.ForwardRefRenderFunction<HTMLInputElement, StepperProps> = (props, ref) => {
    const {
        className,
        disabled,
        max = DEFAULT_MAX_LIMIT,
        min = DEFAULT_MIN_LIMIT,
        name,
        onBlur,
        onChange,
        onFocus,
        readOnly,
        required,
        size: customSize,
        step = 1,
        value: initialValue,
        suffix,
        keyboardEntryDisabled,
        inline,
        error,
        precision,
        decimalSeparator,
        groupSeparator,
        ...other
    } = props;

    const { size } = useComponentProps('Stepper', { size: customSize });

    const isInteractionDisabled = readOnly || disabled;

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const [value, setValue] = useState<InputValue>(initialValue || 0);
    const interval = useRef<number | null>(null);
    const timer = useRef<number | null>(null);

    const updateValueInInterval = (action: string) =>
        (interval.current = window.setInterval(() => {
            setValue((previousValue = 0) =>
                getBoundedValue(action === MouseEventAction.INCREMENT ? +previousValue + step : +previousValue - step)
            );
        }, MOUSE_DOWN_INTERVAL_CHANGE_SPEED));

    const handleMouseDown = (event: React.MouseEvent, action: MouseEventAction) => {
        if (('button' in event && event.button !== 0) || readOnly || disabled) {
            return;
        }

        timer.current = window.setTimeout(() => updateValueInInterval(action), MOUSE_DOWN_INITIAL_CHANGE_DELAY);
    };

    const handleMouseUp = (event: React.MouseEvent | React.KeyboardEvent, action: MouseEventAction) => {
        if (
            ('button' in event && event.button !== 0) ||
            (typeof value === 'string' && value !== '') ||
            readOnly ||
            disabled
        ) {
            return;
        }

        handleValueChange(event, action === MouseEventAction.INCREMENT ? (value || 0) + step : (value || 0) - step);
    };

    const handleValueChange: HandleStepperValueChange = (e, inputValue) => {
        if (typeof inputValue === 'number') {
            const boundedValue = getBoundedValue(inputValue);
            setValue(boundedValue);
            if (onChange && e) {
                onChange(e, boundedValue);
            }
        }

        clearTimers();
    };

    const handleChangeEvent: StepperHandleChange = (e, inputValue) => {
        if (isInteractionDisabled) {
            return;
        }

        setValue(inputValue);
        if (onChange && e) {
            onChange(e, inputValue);
        }

        clearTimers();
    };

    const clearTimers = () => {
        window.clearInterval(interval.current ?? 0);
        window.clearTimeout(timer.current ?? 0);
        timer.current = null;
    };

    const handleMouseLeave = (event: React.MouseEvent, action: MouseEventAction) => {
        if (timer.current) {
            handleMouseUp(event, action);
        }
    };

    const getBoundedValue = (valueToBound: number) => Math.max(min, Math.min(valueToBound, max));

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === KeyValue.ArrowUp) {
            handleMouseUp(event, MouseEventAction.INCREMENT);
        } else if (event.key === KeyValue.ArrowDown) {
            handleMouseUp(event, MouseEventAction.DECREMENT);
        } else if (keyboardEntryDisabled) {
            event.preventDefault();
        }
    };

    const isDecrementButtonDisabled = readOnly || disabled || +value <= min;
    const isIncrementButtonDisabled = readOnly || disabled || +value >= max;

    return (
        <ContainerElement size={size} className={className} {...other}>
            <CounterButtonWrapper
                disabled={isDecrementButtonDisabled}
                onMouseUp={(event) => handleMouseUp(event, MouseEventAction.DECREMENT)}
            >
                <BareIconButton
                    icon={IconName.MinusSimple}
                    onMouseDown={(event) => handleMouseDown(event, MouseEventAction.DECREMENT)}
                    onMouseLeave={(event) => handleMouseLeave(event, MouseEventAction.DECREMENT)}
                    disabled={isDecrementButtonDisabled}
                    tabIndex={-1}
                    data-test-button-minus
                    data-test-id="button-minus-sign"
                />
            </CounterButtonWrapper>
            <FlexInputContainer>
                <NumericFormat
                    name={name}
                    getInputRef={ref}
                    decimalScale={precision}
                    thousandSeparator={groupSeparator}
                    decimalSeparator={decimalSeparator}
                    customInput={Input}
                    autoComplete="off"
                    fixedDecimalScale
                    onKeyDown={handleKeyDown}
                    onValueChange={(values, sourceInfo) => {
                        if (typeof values.floatValue === 'number') {
                            handleChangeEvent(sourceInfo.event as StepperEvent, values.floatValue);
                        }
                        if (!values.floatValue && values.floatValue !== 0) {
                            handleValueChange(sourceInfo.event as StepperEvent, '');
                        }
                    }}
                    size={size}
                    isAllowed={({ floatValue }) =>
                        typeof floatValue === 'number'
                            ? floatValue <= DEFAULT_MAX_LIMIT && floatValue >= DEFAULT_MIN_LIMIT
                            : true
                    }
                    defaultValue={value}
                    value={value}
                    error={error}
                    disabled={disabled}
                    readOnly={readOnly}
                    inline={inline}
                    {...other}
                />
            </FlexInputContainer>
            <CounterButtonWrapper
                onMouseUp={(event) => handleMouseUp(event, MouseEventAction.INCREMENT)}
                disabled={isIncrementButtonDisabled}
            >
                <BareIconButton
                    icon={IconName.PlusSimple}
                    onMouseDown={(event) => handleMouseDown(event, MouseEventAction.INCREMENT)}
                    onMouseLeave={(event) => handleMouseLeave(event, MouseEventAction.INCREMENT)}
                    disabled={isIncrementButtonDisabled}
                    tabIndex={-1}
                    data-test-button-plus
                    data-test-id="button-plus-sign"
                />
            </CounterButtonWrapper>
        </ContainerElement>
    );
};

const Stepper = Field(forwardRef(StepperElement));

export default Stepper;
